import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Phone, FileText, CheckCircle, Clock, User, Calendar, Building, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import apiClient from '../api/client';
import { CallbackRequest, QuoteRequest, CompanyDto } from '../types/dtos';

export default function Support() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as 'callbacks' | 'quotes') || 'callbacks';
  const [activeTab, setActiveTab] = useState<'callbacks' | 'quotes'>(initialTab);
  const [callbacks, setCallbacks] = useState<CallbackRequest[]>([]);
  const [quotes, setQuotes] = useState<QuoteRequest[]>([]);
  const [companies, setCompanies] = useState<CompanyDto[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>('');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isSuperAdmin = user?.company === 'Xtrem Group';

  // Sync tab with URL
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'callbacks' || tab === 'quotes') {
      setActiveTab(tab);
    }
  }, [searchParams]);

  // --- Fetch Base Data ---
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const profileRes = await apiClient.get('/admin/profile');
        setUser(profileRes.data);

        if (profileRes.data.company === 'Xtrem Group') {
          const companiesRes = await apiClient.get<CompanyDto[]>('/admin/companies');
          setCompanies(companiesRes.data);
        }
      } catch (err: any) {
        console.error("Failed to load initial data", err);
        setError(err.response?.data?.message || "Failed to load management tools.");
      }
    };
    fetchInitialData();
  }, []);

  // --- Fetch Support Data (depends on selectedCompany) ---
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const companyParam = selectedCompany ? `?companyId=${selectedCompany}` : '';
        const [callbacksRes, quotesRes] = await Promise.all([
          apiClient.get<CallbackRequest[]>(`/admin/support/callbacks${companyParam}`),
          apiClient.get<QuoteRequest[]>(`/admin/support/quotes${companyParam}`)
        ]);
        setCallbacks(callbacksRes.data);
        setQuotes(quotesRes.data);
      } catch (error: any) {
        console.error("Failed to load support data", error);
        setError(error.response?.data?.message || "Failed to load support data.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [selectedCompany]);

  // --- Actions ---
  const handleMarkComplete = async (id: string) => {
    try {
      await apiClient.put(`/admin/support/callbacks/${id}/complete`);
      setCallbacks(prev => prev.map(req =>
        req.id === id ? { ...req, status: 'Completed' } : req
      ));
      toast.success("Callback marked as completed");
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString() + ' ' +
      new Date(dateString).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="w-full">
      <div className="max-w-7xl mx-auto pt-6 pb-8">

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <h1 className="text-2xl font-bold text-gray-900">Support Requests</h1>

            <div className="flex flex-wrap items-center gap-3">
              {isSuperAdmin && (
                <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-lg px-3 py-1.5 shadow-sm">
                  <Building size={16} className="text-gray-400" />
                  <select
                    value={selectedCompany}
                    onChange={(e) => setSelectedCompany(e.target.value)}
                    className="text-sm font-medium focus:outline-none bg-transparent"
                  >
                    <option value="">All Companies</option>
                    {companies.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>
              )}

              <div className="flex space-x-1 bg-white p-1 rounded-lg border border-gray-200 shadow-sm">
                <button
                  onClick={() => setActiveTab('callbacks')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'callbacks'
                    ? 'bg-blue-900 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <Phone size={14} /> Callbacks
                  <span className={`px-1.5 rounded text-[10px] ${activeTab === 'callbacks' ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>
                    {callbacks.filter(c => c.status === 'Pending').length}
                  </span>
                </button>
                <button
                  onClick={() => setActiveTab('quotes')}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-2 ${activeTab === 'quotes'
                    ? 'bg-blue-900 text-white shadow-sm'
                    : 'text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <FileText size={14} /> Quotes
                  <span className={`px-1.5 rounded text-[10px] ${activeTab === 'quotes' ? 'bg-white/20' : 'bg-gray-100 text-gray-600'}`}>
                    {quotes.filter(q => q.status === 'Pending').length}
                  </span>
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {loading ? (
              <div className="p-12 text-center text-gray-400 flex flex-col items-center gap-3">
                <Clock size={32} className="animate-pulse" />
                Loading support data...
              </div>
            ) : error ? (
              <div className="p-12 text-center text-red-600 font-medium whitespace-pre-wrap">
                {error}
              </div>
            ) : activeTab === 'callbacks' ? (
              // --- CALLBACKS TABLE ---
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                    <tr>
                      <th className="px-6 py-4">Status</th>
                      <th className="px-6 py-4">Customer</th>
                      <th className="px-6 py-4">Product Interest</th>
                      <th className="px-6 py-4">Preferred Time</th>
                      <th className="px-6 py-4">Requested At</th>
                      <th className="px-6 py-4 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {callbacks.length === 0 ? (
                      <tr><td colSpan={6} className="p-12 text-center text-gray-500">No callback requests found.</td></tr>
                    ) : (
                      callbacks.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            {req.status === 'Completed' ? (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                <CheckCircle size={12} className="mr-1" /> Completed
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Clock size={12} className="mr-1" /> Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="font-medium text-gray-900">{req.customerName}</div>
                            <div className="text-gray-500 text-xs">{req.customerPhone}</div>
                          </td>
                          <td className="px-6 py-4 text-blue-700 font-medium">{req.productName}</td>
                          <td className="px-6 py-4 text-gray-600">{req.preferredTime}</td>
                          <td className="px-6 py-4 text-gray-500">{formatDate(req.createdAt)}</td>
                          <td className="px-6 py-4 text-right">
                            {req.status === 'Pending' && (
                              <button
                                onClick={() => handleMarkComplete(req.id)}
                                className="text-blue-600 hover:text-white hover:bg-blue-600 text-xs font-medium border border-blue-200 px-3 py-1 rounded transition-all"
                              >
                                Mark Done
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            ) : (
              // --- QUOTES TABLE ---
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-gray-50 text-gray-600 font-medium border-b">
                    <tr>
                      <th className="px-6 py-4">Ref ID</th>
                      <th className="px-6 py-4">Customer</th>
                      <th className="px-6 py-4">Product</th>
                      <th className="px-6 py-4 w-1/3">Details Provided</th>
                      <th className="px-6 py-4">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {quotes.length === 0 ? (
                      <tr><td colSpan={5} className="p-12 text-center text-gray-500">No quote requests found.</td></tr>
                    ) : (
                      quotes.map((req) => (
                        <tr key={req.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4 font-mono text-xs text-gray-500">{req.requestId}</td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-3">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-bold">
                                {req.customerName.charAt(0)}
                              </div>
                              <div>
                                <div className="font-medium text-gray-900">{req.customerName}</div>
                                <div className="text-gray-500 text-xs">{req.emailAddress}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-gray-900 font-medium">{req.productName}</td>
                          <td className="px-6 py-4 text-gray-600 text-xs">{req.notes || "No notes."}</td>
                          <td className="px-6 py-4 text-gray-500">{new Date(req.createdAt).toLocaleDateString()}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>
      </div>
    </div>
  );
}
